import { useToast } from 'lib/hooks';
import { PrivateFileFolder, PublicFileFolder } from 'lib/types/s3-files';
import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  S3PrivateFileUploader,
  S3PublicFileUploader,
  UploadSuccessHandler,
} from '../helpers';
import { ISingleFileUploadProps, SingleFileUpload } from './SingleFileUpload';

export interface IS3SingleFileUploadProps
  extends Omit<ISingleFileUploadProps, 'onChange'> {
  folder: PrivateFileFolder | PublicFileFolder;
  onAfterUpload?: () => void;
  onRemove?: () => void;
  onStartUpload?: () => void;
  onSuccess: UploadSuccessHandler;
  renderAfter?: (props: {
    abortUpload: () => void;
    uploadPercentage: number;
  }) => ReactElement;
  Uploader?: typeof S3PrivateFileUploader | typeof S3PublicFileUploader;
}

export const S3SingleFileUpload = ({
  accept,
  children,
  className,
  errorMessage,
  file: passedFile,
  folder,
  isDisabled,
  isInvalid,
  label,
  maxFileSizeInMb,
  onAfterUpload,
  onRemove,
  onStartUpload,
  onSuccess,
  previewUrl,
  renderAfter,
  Uploader = S3PrivateFileUploader,
}: IS3SingleFileUploadProps) => {
  const { message } = useToast();

  const uploaderRef = useRef<S3PrivateFileUploader | S3PublicFileUploader>();
  const passedFileRef = useRef<File>(passedFile);

  const [file, setFile] = useState<File>(passedFile);
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  // If the `passedFile` gets updated (let's say because it loads async), we
  // need to update the file (similar to `enableReinitialize` in Formik). It's
  // fine to use name here because deep equality check is not possible and
  // `passedFile` names should never match.
  useEffect(() => {
    if (passedFileRef.current?.name !== passedFile?.name) {
      passedFileRef.current = passedFile;
      setFile(passedFile);
    }
  }, [passedFile]);

  const handleCancelUpload = () => uploaderRef.current.abort();

  const handleChangeFile = async (changedFile: File) => {
    if (!changedFile) {
      handleRemoveFile();
      return;
    }

    setFile(changedFile);

    const uploader = (uploaderRef.current = new Uploader({
      accept,
      domain: 'ADMIN',
      file: changedFile,
      folder,
    }));

    uploader.onAbort = () => {
      setFile(passedFile);
      onAfterUpload?.();
    };

    uploader.onError = (errorMessage?: string) => {
      setFile(passedFile);
      message.error(errorMessage || 'Upload failed, please try again');
      onAfterUpload?.();
    };

    uploader.onProgress = setUploadPercentage;

    uploader.onSuccess = (file) => {
      onSuccess(file);
      message.success('File successfully uploaded');
      onAfterUpload?.();
    };

    onStartUpload?.();
    await uploader.uploadFile();
  };

  const handleRemoveFile = () => {
    setFile(undefined);
    onRemove?.();
  };

  return (
    <>
      <SingleFileUpload
        accept={accept}
        className={className}
        errorMessage={errorMessage}
        file={file}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        label={label}
        maxFileSizeInMb={maxFileSizeInMb}
        onChange={handleChangeFile}
        previewUrl={previewUrl}
      >
        {children}
      </SingleFileUpload>
      {renderAfter?.({
        abortUpload: handleCancelUpload,
        uploadPercentage,
      })}
    </>
  );
};
